import { Injectable, inject, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  Purchases,
  LOG_LEVEL,
  type CustomerInfo,
  type PurchasesOfferings,
  type PurchasesPackage,
  type MakePurchaseResult,
  type PurchasesCallbackId
} from '@revenuecat/purchases-capacitor';
import {
  RevenueCatUI,
  PAYWALL_RESULT,
  type PaywallResult
} from '@revenuecat/purchases-capacitor-ui';
import { DebugLoggerService } from './debug-logger.service';

const RC_ANDROID_PUBLIC_API_KEY = 'goog_QuHykOAwZLxgUryNmyzNVObhfwU';
const RC_IOS_PUBLIC_API_KEY = 'appl_hVrJuCZVOruhzvMekIJdQBfkmUy';
const ENTITLEMENT_ID = 'AforoPro Unlimited';

export interface RevenueCatEntitlementState {
  isActive: boolean;
  productId?: string;
  expiresAt?: number | null;
}

@Injectable({ providedIn: 'root' })
export class RevenueCatService {
  private debugLogger = inject(DebugLoggerService);

  private configured = false;
  private customerInfoListenerId: PurchasesCallbackId | null = null;

  private readonly _customerInfo = signal<CustomerInfo | null>(null);
  readonly customerInfo = this._customerInfo.asReadonly();

  private readonly _isNativeAvailable = signal(false);
  readonly isNativeAvailable = this._isNativeAvailable.asReadonly();

  private customerInfoCallback: ((info: CustomerInfo) => void) | null = null;

  async configure(deviceId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.debugLogger.log('RevenueCat', 'Skipping configuration on non-native platform');
      return;
    }

    if (this.configured) {
      this.debugLogger.log('RevenueCat', 'Already configured, skipping');
      return;
    }

    try {
      const apiKey = this.getPublicApiKeyForPlatform();
      if (!apiKey) {
        this.debugLogger.warn('RevenueCat', 'Public API key missing/invalid for current platform');
        return;
      }

      const appInfo = await App.getInfo().catch(() => null);
      const isDebugBuild = appInfo?.id?.endsWith('.debug') ?? false;
      await Purchases.setLogLevel({ level: isDebugBuild ? LOG_LEVEL.DEBUG : LOG_LEVEL.WARN });
      await Purchases.configure({ apiKey });
      await Purchases.logIn({ appUserID: deviceId });

      this.configured = true;
      this._isNativeAvailable.set(true);
      this.debugLogger.log('RevenueCat', `Configured with deviceId=${deviceId}`);

      const { customerInfo } = await Purchases.getCustomerInfo();
      this._customerInfo.set(customerInfo);
    } catch (error) {
      this.debugLogger.error('RevenueCat', 'Configuration failed', error);
    }
  }

  private getPublicApiKeyForPlatform(): string | null {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      return RC_ANDROID_PUBLIC_API_KEY.startsWith('goog_') ? RC_ANDROID_PUBLIC_API_KEY : null;
    }
    if (platform === 'ios') {
      return RC_IOS_PUBLIC_API_KEY.startsWith('appl_') ? RC_IOS_PUBLIC_API_KEY : null;
    }
    return null;
  }

  async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.configured) return null;
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      this._customerInfo.set(customerInfo);
      return customerInfo;
    } catch (error) {
      this.debugLogger.error('RevenueCat', 'getCustomerInfo failed', error);
      return this._customerInfo();
    }
  }

  checkEntitlement(customerInfo?: CustomerInfo | null): RevenueCatEntitlementState {
    const info = customerInfo ?? this._customerInfo();
    if (!info) {
      return { isActive: false };
    }

    const entitlement = info.entitlements.active[ENTITLEMENT_ID];
    if (!entitlement) {
      return { isActive: false };
    }

    return {
      isActive: true,
      productId: entitlement.productIdentifier ?? undefined,
      expiresAt: entitlement.expirationDateMillis ?? null
    };
  }

  async refreshAndCheckEntitlement(): Promise<RevenueCatEntitlementState> {
    const info = await this.getCustomerInfo();
    return this.checkEntitlement(info);
  }

  async getOfferings(): Promise<PurchasesOfferings | null> {
    if (!this.configured) return null;
    try {
      return await Purchases.getOfferings();
    } catch (error) {
      this.debugLogger.error('RevenueCat', 'getOfferings failed', error);
      return null;
    }
  }

  async purchasePackage(pkg: PurchasesPackage): Promise<MakePurchaseResult | null> {
    if (!this.configured) return null;
    try {
      const result = await Purchases.purchasePackage({ aPackage: pkg });
      this._customerInfo.set(result.customerInfo);
      return result;
    } catch (error: any) {
      if (error?.code === '1' || error?.userCancelled) {
        this.debugLogger.log('RevenueCat', 'User cancelled purchase');
        return null;
      }
      this.debugLogger.error('RevenueCat', 'purchasePackage failed', error);
      throw error;
    }
  }

  async restorePurchases(): Promise<CustomerInfo | null> {
    if (!this.configured) return null;
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      this._customerInfo.set(customerInfo);
      return customerInfo;
    } catch (error) {
      this.debugLogger.error('RevenueCat', 'restorePurchases failed', error);
      return null;
    }
  }

  async presentPaywall(): Promise<PaywallResult | null> {
    if (!this.configured) return null;
    try {
      const offerings = await this.getOfferings();
      const offering =
        offerings?.current ??
        Object.values(offerings?.all ?? {})[0] ??
        null;

      if (!offering) {
        this.debugLogger.error(
          'RevenueCat',
          'No offering available for paywall. Check RevenueCat current offering/products configuration.'
        );
        return null;
      }

      const result = await RevenueCatUI.presentPaywallIfNeeded({
        requiredEntitlementIdentifier: ENTITLEMENT_ID,
        offering
      });

      if (result.result === PAYWALL_RESULT.PURCHASED || result.result === PAYWALL_RESULT.RESTORED) {
        const info = await this.getCustomerInfo();
        this._customerInfo.set(info);
      }

      return result;
    } catch (error: any) {
      const details = {
        code: error?.code ?? error?.userInfo?.code ?? null,
        message: error?.message ?? null,
        underlyingErrorMessage: error?.underlyingErrorMessage ?? error?.userInfo?.underlyingErrorMessage ?? null,
        userInfo: error?.userInfo ?? null
      };
      this.debugLogger.error('RevenueCat', 'presentPaywall failed', details);
      return null;
    }
  }

  async presentCustomerCenter(): Promise<void> {
    if (!this.configured) return;
    try {
      await RevenueCatUI.presentCustomerCenter();
      await this.getCustomerInfo();
    } catch (error) {
      this.debugLogger.error('RevenueCat', 'presentCustomerCenter failed', error);
    }
  }

  onCustomerInfoUpdated(callback: (info: CustomerInfo) => void): void {
    this.customerInfoCallback = callback;
  }

  async startListeningForUpdates(): Promise<void> {
    if (!this.configured || this.customerInfoListenerId) return;
    try {
      this.customerInfoListenerId = await Purchases.addCustomerInfoUpdateListener(
        (customerInfo: CustomerInfo) => {
          this._customerInfo.set(customerInfo);
          this.customerInfoCallback?.(customerInfo);
        }
      );
    } catch (error) {
      this.debugLogger.error('RevenueCat', 'Failed to start customer info listener', error);
    }
  }

  async stopListeningForUpdates(): Promise<void> {
    if (this.customerInfoListenerId) {
      try {
        await Purchases.removeCustomerInfoUpdateListener({
          listenerToRemove: this.customerInfoListenerId
        });
      } catch { /* ignore */ }
      this.customerInfoListenerId = null;
    }
  }
}
